import {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useDispatch} from "react-redux";
import {Card} from "../basic/card";
import {Button} from "../basic/button";
import {Input} from "../basic/input";
import {Label} from "../basic/label";
import {Separator} from "../basic/separator";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../basic/select";
import {useTranslationWithKeys} from "../../hooks/useTranslationWithKeys";
import {useGetPortalTeamQuery, useUpdatePortalTeamMutation} from "../../store/generated/api.ts";
import {toast} from "sonner";
import {setTeamUpdatePending} from "../../store/updatesSlice";

interface TeamSettingsProps {
    phoneNumberRequired?: boolean;
}

export function TeamSettings({phoneNumberRequired = true}: TeamSettingsProps) {
    const {t} = useTranslationWithKeys();
    const dispatch = useDispatch();
    const {data, isLoading, error, refetch} = useGetPortalTeamQuery({
        page: 0,
        size: 10
    });
    const team = data?.team;

     const [updatePortalTeam, {isLoading: isUpdating}] = useUpdatePortalTeamMutation();
     const [invoiceLanguage, setInvoiceLanguage] = useState<"en" | "de" | "fr" | "">("en");

     const {
        register,
        handleSubmit,
        reset,
        formState: {isDirty, errors}
    } = useForm<{
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        invoiceLanguage: string;
        vatNumber?: string;
    }>({
        mode: "onBlur"
    });

     useEffect(() => {
         if (team) {
             reset({
                 teamName: team.name || "",
                 streetAddress: team.streetAddress || "",
                 city: team.city || "",
                 stateProvince: team.stateProvince || "",
                 postalCode: team.postalCode || "",
                 country: team.country || "",
                 phoneNumber: team.phoneNumber || "",
                 billingEmail: team.billingEmail || "",
                 invoiceLanguage: "",
                 vatNumber: team.vatNumber || ""
             });
             setInvoiceLanguage(team.invoiceLanguage as "en" | "de" | "fr" || "en");
         }
     }, [team?.id, team?.name, team?.invoiceLanguage, reset]);

    const onSubmit = async (data: {
        teamName: string;
        streetAddress: string;
        city: string;
        stateProvince: string;
        postalCode: string;
        country: string;
        phoneNumber: string;
        billingEmail: string;
        invoiceLanguage: string;
        vatNumber?: string;
    }) => {
         try {
             dispatch(setTeamUpdatePending(true));

             await updatePortalTeam({
                vwpTeamUpdateRequest: {
                    name: data.teamName,
                    billingStreetAddress: data.streetAddress,
                    billingCity: data.city,
                    billingState: data.stateProvince,
                    billingPostalCode: data.postalCode,
                    billingCountry: data.country,
                    phone: data.phoneNumber,
                    billingEmail: data.billingEmail,
                    invoiceLanguage: (invoiceLanguage || undefined) as "en" | "de" | "fr" | undefined,
                    vatNumber: data.vatNumber
                }
            }).unwrap();

            toast.success(t('settings.teamSettingsSaved'));
            await refetch();
        } catch (error: any) {
            if (error?.data?.error === 'INVALID_COUNTRY') {
                const invalidCountry = error?.data?.details?.country || data.country;
                toast.error(t('settings.invalidCountry', {country: invalidCountry}));
            } else {
                toast.error(t('error.generic', {message: t('common.error')}));
            }
        } finally {
            dispatch(setTeamUpdatePending(false));
        }
    };

    if (isLoading) {
        return (
            <Card className="p-6 shadow-sm border-border/50">
                <div className="flex items-center justify-center h-32">
                    <p className="text-muted-foreground">{t('common.loading')}</p>
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="p-6 shadow-sm border-border/50">
                <div className="flex items-center justify-center h-32">
                    <p className="text-destructive">{t('settings.loadTeamError')}</p>
                </div>
            </Card>
        );
    }

    return (
        <Card className="p-6 shadow-sm border-border/50">
            <h3 className="mb-4">{t('settings.teamInformation')}</h3>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="teamName">{t('settings.teamName')}</Label>
                    <Input id="teamName" {...register("teamName")} />
                </div>
                <div className="space-y-2">
                    <Label htmlFor="streetAddress">{t('settings.streetAddress')}</Label>
                    <Input id="streetAddress" {...register("streetAddress")}/>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="city">{t('settings.city')}</Label>
                        <Input id="city" {...register("city")}/>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="stateProvince">{t('settings.stateProvince')}</Label>
                        <Input id="stateProvince" {...register("stateProvince")}/>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="postalCode">{t('settings.postalCode')}</Label>
                        <Input id="postalCode" {...register("postalCode")}/>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="country">{t('settings.country')}</Label>
                        <Input id="country" {...register("country")}/>
                    </div>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="phoneNumber">{t('settings.phoneNumber')}</Label>
                    <Input
                        id="phoneNumber" {...register("phoneNumber", phoneNumberRequired ? {required: t('settings.phoneNumberRequired')} : {})} />
                    {errors.phoneNumber && <p className="text-destructive text-sm">{errors.phoneNumber.message}</p>}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="billingEmail">{t('settings.billingEmail')}</Label>
                    <Input id="billingEmail" type="email" {...register("billingEmail")}/>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="invoiceLanguage">{t('settings.invoiceLanguage')}</Label>
                     <Select
                         value={invoiceLanguage || "en"}
                         onValueChange={(value) => {
                             setInvoiceLanguage(value as "en" | "de" | "fr");
                         }}
                    >
                        <SelectTrigger id="invoiceLanguage">
                            <SelectValue placeholder="Select language"/>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="en">English</SelectItem>
                            <SelectItem value="de">Deutsch</SelectItem>
                            <SelectItem value="fr">Francais</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                     <Label htmlFor="vatNumber">{t('settings.vatNumber')}</Label>
                     <Input id="vatNumber" {...register("vatNumber")}/>
                     <input type="hidden" {...register("invoiceLanguage")} />
                 </div>
                <Separator className="my-6"/>
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={!isDirty || isUpdating}
                        className="rounded-full px-8"
                    >
                        {isUpdating ? t('common.action.saving') : t('settings.saveChanges')}
                    </Button>
                </div>
            </form>
        </Card>
    );
}

